import {
  getFooterData,
  getHeaderData,
  getSisNavigationMenu,
  getTaxonomyData,
  getTransversalData,
} from '@digital-retail/falabella-ui-cross-components';
import { getUrlParts, isSodimacRetail, isStandalone, isTottusStandalone } from '@digital-retail/store-manager';
import { cache } from '../@lib/cache';
import apiConfig from '../config/api/apiConfig';
import { constants } from '../config/constants';
import { geoFallback } from '../data/fallbacks/geo';
import { siteConfig } from '../static/siteConfig.default';
import { Logger } from './Logger';
import { httpService } from './httpService';

const fetchConfigFromApi = async (config, appCtx, url, headers = {}, opt = {}, fallback = {}) => {
  if (config.ENABLE_MEM_CACHE) {
    const valueFromCache = await cache.get(url);
    if (!valueFromCache) {
      const valueFromApi = await httpService(appCtx).get(url, {
        headers,
        ...opt,
      });
      await cache.set(url, valueFromApi);
      return valueFromApi;
    }
    return valueFromCache;
  }
  return httpService(appCtx).get(
    url,
    {
      headers,
      ...opt,
    },
    'text',
    fallback
  );
};

const isZoneDataInvalid = (zoneData = {}, isTottus = false, store = '') => {
  const { politicalId, priceGroupId, zones = [], expressZones = [], standardZones = [] } = zoneData;

  const hasRequiredFields =
    politicalId && priceGroupId && Array.isArray(zones) && (!isSodimacRetail(store) ? zones.length > 0 : true);
  if (!hasRequiredFields) {
    return true;
  }
  const hasValidExpressAndStandardZones = Array.isArray(expressZones) && Array.isArray(standardZones);

  if (isTottus && !hasValidExpressAndStandardZones) {
    return true;
  }

  return false;
};

const appServices = (publicRuntimeConfig, serverRuntimeConfig, appCtx) => ({
  siteConfig: {
    init: async () => {
      const { getAccessToken } = serverRuntimeConfig;
      const { regionCode, store } = appCtx;
      const st = store?.toLowerCase() || 'marketplace';
      const tokenResponse = await getAccessToken({
        tenant: regionCode,
        store: st,
        context: 'browse',
      });
      const url = `${
        publicRuntimeConfig.SITE_CONFIG_URL
      }/${st.toLowerCase()}?country=${regionCode}&parseJson=true&merge=true`;

      return fetchConfigFromApi(serverRuntimeConfig, appCtx, url, {
        Authorization: tokenResponse,
      });
    },
    resolve: ({ data, error }) => {
      if (error) {
        Logger.error(`Using fallback site-config => ${JSON.stringify(error)}`);
        return siteConfig[appCtx.regionCode || 'cl'][appCtx.store || 'marketplace'];
      }
      return data;
    },
  },
  // to be removed
  zonesInfo: {
    init: () => {
      const { regionCode } = appCtx;
      // Changed this call to new API
      const getLocationsUrl = apiConfig.getLocationsUrl({
        base: publicRuntimeConfig.CLUSTER_CUSTOMER_LOCATION_HOST,
        siteId: regionCode,
      });

      return fetchConfigFromApi(
        serverRuntimeConfig,
        appCtx,
        getLocationsUrl,
        {},
        {},
        { data: geoFallback.zones.v1[regionCode] }
      );
    },
    resolve: ({ data, error }) => {
      if (error || !data?.data) {
        Logger.error(`Using default zones => ${JSON.stringify(error)}`);
        return {
          zoneID: constants.ZONE.DEFAULT_ZONE_ID,
          zoneName: constants.ZONE.DEFAULT_ZONE_NAME,
        };
      }
      const { defaultZone, availableZones, data: zones } = data;
      const zoneItem = zones.find((zone) => zone.id === defaultZone);
      const zoneName = zoneItem?.name;
      return { zoneID: defaultZone, zoneName, zones, availableZones };
    },
  },
  transversalBanner: {
    init: async () => {
      return getTransversalData({
        tenant: appCtx.regionCode,
        baseUrl: publicRuntimeConfig.CLUSTER_CONTENT_API_HOST,
      });
    },
    resolve: (data) => {
      return data;
    },
  },
  header: {
    init: async () => {
      return getHeaderData({
        tenant: appCtx.regionCode,
        baseUrl: publicRuntimeConfig.CLUSTER_CONTENT_API_HOST,
        store: appCtx.store,
      });
    },
    resolve: (data) => {
      return data;
    },
  },
  sisNavigationMenu: {
    init: async () => {
      return appCtx.store
        ? getSisNavigationMenu({
            tenant: appCtx.regionCode,
            baseUrl: publicRuntimeConfig.CLUSTER_CONTENT_API_HOST,
            store: appCtx.store,
          })
        : {};
    },
    resolve: (data) => {
      return data;
    },
  },
  footer: {
    init: async () => {
      return getFooterData({
        tenant: appCtx.regionCode,
        baseUrl: publicRuntimeConfig.CLUSTER_CONTENT_API_HOST,
        store: appCtx.store,
      });
    },
    resolve: (data) => {
      return data;
    },
  },
  taxonomy: {
    init: async () => {
      return getTaxonomyData({
        tenant: appCtx.regionCode,
        baseUrl: publicRuntimeConfig.CLUSTER_CONTENT_API_HOST,
      });
    },
    resolve: (data) => {
      return data;
    },
  },
  comuna: {
    init: () => {
      const { store, regionCode, pid, latLong, isTottusSA } = appCtx;
      const environment = publicRuntimeConfig.ENVIRONMENT || 'production';
      const { origin } = getUrlParts({
        store: isStandalone({ store }) || isSodimacRetail(store) ? store : undefined,
        environment,
        regionCode,
      });
      const base = `${origin}${publicRuntimeConfig.CL_ENDPOINT}`;
      const getAllZones = isTottusSA;
      const getComunaUrl = apiConfig.getDistrictInfo({
        base,
        siteId: regionCode,
        politicalId: pid,
        latLong,
        getAllZones,
        store,
      });
      return fetchConfigFromApi(serverRuntimeConfig, appCtx, getComunaUrl);
    },
    resolve: ({ data, error }) => {
      const { regionCode, store } = appCtx;
      const zoneData = data?.data ?? {};
      const isTottusSA = isTottusStandalone(store);
      if (isZoneDataInvalid(zoneData, isTottusSA, store)) {
        Logger.error(`Using hardcoded default zones from catch block => ${JSON.stringify(error)}`);
        return {
          data: constants.DEF_ZONE[regionCode][process.env.ENVIRONMENT],
          default: true,
          fromCatch: true,
        };
      }
      return data;
    },
  },
  virtualZones: {
    init: () => {
      const { regionCode, pid, isSodimacRE, store, zones } = appCtx;

      let hasValidZones = false;
      const zonesData = zones
        .replace(/[[\]"]+/g, '')
        .split(',')
        .map((z) => z.trim())
        .filter((z) => z !== '');

      if (zones) {
        try {
          hasValidZones = Array.isArray(zonesData) && zonesData.length > 0;
        } catch {
          hasValidZones = false;
        }
      }
      if (isSodimacRE && !hasValidZones) {
        const environment = publicRuntimeConfig.ENVIRONMENT || 'production';

        const { origin } = getUrlParts({ store, environment, regionCode });
        const base = `${origin}${publicRuntimeConfig.CL_ENDPOINT}`;
        const getComunaUrl = apiConfig.getVirtualZonesUrl({
          base,
          regionCode,
          politicalId: pid,
        });

        return fetchConfigFromApi(serverRuntimeConfig, appCtx, getComunaUrl);
      }
      return {
        data: [],
      };
    },
    resolve: ({ data, error }) => {
      if (error) {
        Logger.error(`Unable to get virtualZones => ${JSON.stringify(error)}`);
        return { data: [] };
      }
      return data;
    },
  },
});
// ! Must be called only on server
const fetchApplicationData = (publicRuntimeConfig, serverRuntimeConfig, appCtx, query) => ({
  fetch: async (services) => {
    const { tracingHeaders } = appCtx;
    const fetcher = appServices(publicRuntimeConfig, serverRuntimeConfig, appCtx, query);

    let data = new Array(services.length).fill().map(() => ({ data: null, error: null }));
    try {
      const promises = services
        .filter((s) => fetcher[s])
        .map((service) => {
          return fetcher[service].init();
        });

      data = await Promise.all(promises);
    } catch (e) {
      Logger.error(`Error Running Promise => ${e}`, tracingHeaders);
    }

    return services.map((service, i) => {
      return fetcher?.[service]?.resolve(data[i]);
    });
  },
});

export { fetchApplicationData, isZoneDataInvalid };
